import { FC, useCallback, useState, type KeyboardEvent } from "react";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import { BrokenImageOutlined } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { CONSTANTS } from "../../../utils/CONSTS";
import { useInstituteContext } from "../../../context/institute.context";
import {
  useReportCardTemplatesHook,
  useUpdateReportCardConfigHook,
} from "../../../hooks/useReportCardHook";
import { useReportCardService } from "../../../apis/reportCard/useReportCardService";
import { mapGetConfigToUpdateRequest } from "../../../apis/reportCard/mapGetConfigToUpdateRequest";
import { logger } from "../../../utils/common";
import { AssessmentInstituteRequiredEmptyState } from "./assessmentInstituteRequiredEmptyState";

const ReportCardTemplatesView: FC = () => {
  const { selectedInstitute } = useInstituteContext();
  const queryClient = useQueryClient();
  const { getConfig } = useReportCardService();
  const { data, isLoading, error } = useReportCardTemplatesHook();
  const updateMutation = useUpdateReportCardConfigHook();
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [brokenThumbs, setBrokenThumbs] = useState<Record<string, boolean>>({});

  const templates = data?.templates ?? [];
  const defaultName = templates.find((t) => t.isDefault)?.name;

  const markThumbBroken = useCallback((name: string) => {
    setBrokenThumbs((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleCancel = () => setPendingName(null);

  const handleSave = async () => {
    if (!pendingName || !selectedInstitute?.id || updateMutation.isPending) {
      return;
    }
    try {
      const config = await queryClient.fetchQuery({
        queryKey: ["report-card-config", selectedInstitute.id],
        queryFn: () => getConfig(),
      });
      const body = mapGetConfigToUpdateRequest(config, {
        defaultTemplate: pendingName,
      });
      await updateMutation.mutateAsync(body);
      await queryClient.refetchQueries({
        queryKey: ["report-card-templates", selectedInstitute.id],
      });
      setPendingName(null);
      logger.show_success("Default report card template saved.");
    } catch (e) {
      logger.show_error(
        e instanceof Error ? e.message : "Could not save template choice.",
      );
    }
  };

  const selectionDirty =
    pendingName !== null && pendingName !== (defaultName ?? "");

  if (!selectedInstitute?.id) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Report card templates`}
        />
        <Box data-testid="report-card-templates-view">
          <AssessmentInstituteRequiredEmptyState
            sectionLabel="report card templates"
            data-testid="report-card-templates-no-institute"
          />
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Report card templates`}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={320}
          data-testid="report-card-templates-view"
        >
          <CircularProgress data-testid="report-card-templates-loading" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle
          title={`${CONSTANTS.title.assessment} - Report card templates`}
        />
        <Box data-testid="report-card-templates-view">
          <Alert severity="error" data-testid="report-card-templates-error">
            Could not load report card templates. Please try again.
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <DocumentTitle
        title={`${CONSTANTS.title.assessment} - Report card templates`}
      />
      <Box data-testid="report-card-templates-view">
        <Typography component="h2" variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Report card templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the layout used for generated report cards. Thumbnails are
          indicative only.
        </Typography>

        {pendingName !== null && (
          <Box
            display="flex"
            gap={1}
            flexWrap="wrap"
            sx={{ mb: 2 }}
            data-testid="report-card-templates-actions"
          >
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={updateMutation.isPending || !selectionDirty}
              data-testid="report-card-templates-save"
            >
              {updateMutation.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={updateMutation.isPending}
              data-testid="report-card-templates-cancel"
            >
              Cancel
            </Button>
          </Box>
        )}

        <Grid container spacing={2}>
          {templates.map((t) => {
            const selected =
              pendingName !== null ? pendingName === t.name : t.isDefault;
            const thumbBroken = brokenThumbs[t.name];

            return (
              <Grid item xs={12} sm={6} md={4} key={t.name}>
                <Card
                  variant="outlined"
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={`Select ${t.displayName} as the default report card template`}
                  sx={{
                    cursor: "pointer",
                    borderColor: selected ? "primary.main" : undefined,
                    borderWidth: selected ? 2 : 1,
                    boxShadow: selected ? 2 : 0,
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: 2,
                    },
                  }}
                  onClick={() => setPendingName(t.name)}
                  onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setPendingName(t.name);
                    }
                  }}
                  data-testid={`report-card-template-card-${t.name}`}
                >
                  {thumbBroken ? (
                    <Box
                      sx={{
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "action.hover",
                      }}
                    >
                      <BrokenImageOutlined color="disabled" fontSize="large" />
                    </Box>
                  ) : (
                    <CardMedia
                      component="img"
                      height="180"
                      image={t.thumbnailUrl}
                      alt={`Preview thumbnail for ${t.displayName}`}
                      sx={{ objectFit: "cover" }}
                      onError={() => markThumbBroken(t.name)}
                    />
                  )}
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t.displayName}
                      {t.isDefault && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="primary"
                          sx={{ ml: 1 }}
                        >
                          (current default)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </>
  );
};

export default ReportCardTemplatesView;
